// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import {
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  input,
  OnChanges,
  OnDestroy,
  output,
  Renderer2,
  signal,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  CdkConnectedOverlay,
  Overlay,
  OverlayModule,
} from '@angular/cdk/overlay';
import { TranslateModule } from '@ngx-translate/core';

import { CtsGantryEventFilterTabEType } from '../../enum/cts-gantry-event-filter-tab-type';

import { CtsGantryInitEvent } from '../../interfaces/cts-gantry-init-event';
import { CtsGantryInitEventEType } from '../../enum/cts-gantry-init-event-type';
import { CtsGantryInitEventStatusService } from '../../services/cts-gantry-init-event-status.service';
import { BehaviorSubject, debounceTime, Subject, takeUntil } from 'rxjs';
import { ScreenLayoutService } from 'src/app/modules/shared/services/screen-layout-service/screen-layout.service';
import { CtsGantryTimeZoneService } from '../../services/cts-gantry-time-zone.service';
import { toSignal } from '@angular/core/rxjs-interop';
import moment from 'moment';
import { CtsGantryTimeZone } from '../../interfaces/cts-gantry-time-zone';

@Component({
  standalone: true,
  imports: [CommonModule, OverlayModule, TranslateModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  selector: 'app-cts-gantry-startup-event-filter',
  templateUrl: './cts-gantry-startup-event-filter.component.html',
  styleUrl: './cts-gantry-startup-event-filter.component.scss',
})
export class CtsGantryStartupEventFilterComponent
  implements OnChanges, OnDestroy
{
  events = input<CtsGantryInitEvent[]>([]);
  selectedEvent = input<CtsGantryInitEvent | undefined>(undefined);
  selectedEventChange = output<CtsGantryInitEvent>();

  @ViewChild(CdkConnectedOverlay) cdkConnectedOverlay:
    | CdkConnectedOverlay
    | undefined;

  private readonly timeZone = toSignal(this.timeZoneService.timeZone());
  protected readonly selectedEventSignal = signal<
    CtsGantryInitEvent | undefined
  >(this.selectedEvent());
  protected readonly computedSelectedEvent = computed(() => {
    const sEvent = this.selectedEventSignal();
    const tz = this.timeZone();
    return this.addFormattedDate(tz, sEvent);
  });

  protected readonly computedEvents = computed(() => {
    const tz = this.timeZone();
    return this.events().map((event) => {
      return this.addFormattedDate(tz, event) ?? event;
    });
  });

  protected readonly ALL_EVENTS_TAB = CtsGantryEventFilterTabEType.ALL_EVENTS;
  protected readonly ERRORS_TAB = CtsGantryEventFilterTabEType.ERRORS;
  protected readonly UNKNOWN = CtsGantryInitEventEType.UNKNOWN;
  protected readonly WHITE_SPACE = ' ';

  private readonly KEY_ESCAPE = 'Escape';
  private readonly overlayOpenSubject = new Subject<boolean>();
  private readonly unsubscribe = new Subject<void>();

  protected isOverlayOpen = false;
  protected activeTab = CtsGantryEventFilterTabEType.ALL_EVENTS;
  protected readonly errorEvents = computed(() => {
    const evts = this.computedEvents();
    return evts.filter(
      (event) =>
        event.type === CtsGantryInitEventEType.COMPLETED_WITH_ERROR ||
        event.type === CtsGantryInitEventEType.INCOMPLETE ||
        event.type === CtsGantryInitEventEType.AUTOREBOOT ||
        event.type === CtsGantryInitEventEType.UNKNOWN,
    );
  });

  protected scrollStrategy = this.overlay.scrollStrategies.close();

  private unlisten: () => void = () => {};

  public constructor(
    private readonly overlay: Overlay,
    private readonly renderer: Renderer2,
    private readonly screenLayoutService: ScreenLayoutService,
    protected readonly ctsGantryInitEventStatusService: CtsGantryInitEventStatusService,
    protected readonly timeZoneService: CtsGantryTimeZoneService,
  ) {
    this.listenToScroll();
    this.listenToOverlayOpen();
    this.listenToScreenSizeChange(this.screenLayoutService.desktopLarge$);
    this.listenToScreenSizeChange(this.screenLayoutService.desktop$);
    this.listenToScreenSizeChange(this.screenLayoutService.tablet$);
    this.listenToScreenSizeChange(this.screenLayoutService.phone$);
  }

  public ngOnChanges(): void {
    const selectedEvent = this.selectedEvent();
    if (selectedEvent) {
      this.selectedEventSignal.set(selectedEvent);
    }
  }

  private addFormattedDate(
    tz: CtsGantryTimeZone | undefined,
    ctsEvent: CtsGantryInitEvent | undefined,
  ) {
    if (ctsEvent) {
      const mValue = moment(ctsEvent.timestamp).utcOffset(
        tz ? tz.offsetInMinutes : ctsEvent.timestampOffset / 60,
      );
      ctsEvent.timestampFormatted = mValue.format('DD MMM YYYY - HH:mm');
    }
    return ctsEvent;
  }

  private listenToScreenSizeChange(
    screenBehaviorSubject: BehaviorSubject<boolean>,
  ): void {
    screenBehaviorSubject.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
      this.cdkConnectedOverlay?.overlayRef?.updatePosition();
    });
  }

  public ngOnDestroy(): void {
    if (this.unlisten) {
      this.unlisten();
    }

    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  private listenToOverlayOpen(): void {
    this.overlayOpenSubject
      .pipe(debounceTime(50), takeUntil(this.unsubscribe))
      .subscribe(this.overlayOpenSubscribeFn);
  }

  private readonly overlayOpenSubscribeFn = (isOpen: boolean) =>
    (this.isOverlayOpen = isOpen);

  protected overlayOpenNext(isOpen: boolean): void {
    this.overlayOpenSubject.next(isOpen);
  }

  protected updateActiveTab(activeTab: CtsGantryEventFilterTabEType): void {
    this.activeTab = activeTab;
  }

  protected onItemClick(event: CtsGantryInitEvent): void {
    this.selectedEventSignal.set(event);
    this.overlayOpenNext(false);
    this.selectedEventChange.emit(event);
  }

  protected onOverlayKeydown(keyboardEvent: KeyboardEvent): void {
    if (this.KEY_ESCAPE === keyboardEvent.key) {
      this.overlayOpenNext(false);
    }
  }

  private listenToScroll(): void {
    const el = document.getElementById('gantry-startup-card');
    const cardWrapper = el?.shadowRoot?.querySelector('slot.empty-footer');
    if (cardWrapper) {
      this.unlisten = this.renderer.listen(cardWrapper, 'scroll', () => {
        this.isOverlayOpen = false;
      });
    }
  }
}
